import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import axios from 'axios';
import { useDispatch } from 'react-redux';
import { Checkbox } from '@mui/material';
import { toast } from 'react-toastify';
import { MinLoader, NotError, SimpleTabs } from 'client/src/components';
import { base64ToFile, FindLoad, FindSuccessItem, User, KeyPurpose } from 'client/src/utils';
import { getFreshFileUrl } from 'client/src/utils/hooks/getFreshFileUrl';
import { Draw } from 'client/src/fragments/schedule/core/common/draw';
import { TypeSignature } from 'client/src/fragments/schedule/core/common/typeSignature';
import { SignatureFileUploader } from 'client/src/fragments/schedule/core/common/signatureFileUploader';
import { UploadedSignature } from 'client/src/fragments/schedule/core/common/uploadedSignature';
import { GuardianSelector } from 'client/src/fragments/schedule/core/common/guardianSelector';
import {
    signatureTabsLabels,
    SIGNATURE_ACTION_TYPE,
    buildSignerPayload,
    isOtherSignerIncomplete,
} from 'client/src/fragments/schedule/core/common/constants';
import { signatureTypeEnums } from 'client/src/fragments/schedule/core/modals/constants';
import { appointmentActions, adminActions, uploadFile } from 'client/src/store';

const SIGNATURE_TAB = {
    DRAW: 'Draw',
    TYPE: 'Type',
    UPLOAD: 'Upload',
};

export const SignaturePanel = forwardRef(
    (
        {
            appt,
            provider = false,
            title,
            showSavedSignature = false,
            showGuardianSelector = false,
            guardianWidth,
            showFooter = false,
            submitLabel = 'Apply Signature',
            onCancel,
            onApplied,
            doNotShowAlreadyUploaded,
            onCanvasRef,
            onNameChange,
            onLoading,
            fromComplete,
            hideSaveDefault,
        },
        ref
    ) => {
        const { userInfo } = User();
        const dispatch = useDispatch();
        const loading = FindLoad(SIGNATURE_ACTION_TYPE);
        const success = FindSuccessItem(SIGNATURE_ACTION_TYPE);
        const savedSignature = userInfo?.signature;
        const hasSavedSignature = !!savedSignature?.url;
        const [activeTab, setActiveTab] = useState(SIGNATURE_TAB.DRAW);
        const [useSaved, setUseSaved] = useState(showSavedSignature && hasSavedSignature);
        const [childLoading, setChildLoading] = useState(false);
        const [selectedGuardian, setSelectedGuardian] = useState('');
        const [otherSigner, setOtherSigner] = useState(null);
        const [showSignerErrors, setShowSignerErrors] = useState(false);
        const [saveAsDefault, setSaveAsDefault] = useState(false);
        const [typedName, setTypedName] = useState('');
        const [uploadedFile, setUploadedFile] = useState(null);
        const [hasDrawing, setHasDrawing] = useState(false);
        const [geoPos, setGeoPos] = useState(null);

        const panelRef = useRef(null);
        const canvasRef = useRef(null);

        const signatureName = provider ? 'provider-signature' : 'signature';

        // Derive current uploaded file for UploadedSignature display
        const currentFile =
            provider ? appt?.providerSignature ? { file: appt.providerSignature } : null
            : appt?.signature
            ? { file: appt.signature }
            : null;

        // Clear canvas on successful submission
        useEffect(() => {
            if (success && success?.data?.type === signatureName) {
                if (canvasRef.current?.current) {
                    canvasRef.current.current.clear();
                }
                setTypedName('');
                setUploadedFile(null);
                setHasDrawing(false);
            }
        }, [success]);

        // Something to submit in the current mode: the saved signature, a stroke, a typed name or a file.
        const hasSignature = useSaved
            ? hasSavedSignature
            : activeTab === SIGNATURE_TAB.DRAW
              ? hasDrawing
              : activeTab === SIGNATURE_TAB.TYPE
                ? !!typedName.trim()
                : !!uploadedFile;

        // Expose internal state to parent via ref (for bulk mode)
        useImperativeHandle(ref, () => ({
            useSaved,
            activeTab,
            savedSignature,
            selectedGuardian,
            otherSigner,
            saveAsDefault,
        }));

        // Called when the Draw tab mounts, i.e. with a blank canvas.
        const handleCanvasRef = (ref) => {
            canvasRef.current = ref;
            setHasDrawing(false);
            onCanvasRef?.(ref);
        };

        const handleNameChange = (name) => {
            setTypedName(name);
            onNameChange?.(name);
        };

        const handleUploadFileChange = (file) => {
            setUploadedFile(file);
        };

        const handleChildLoading = (isLoading) => {
            setChildLoading(isLoading);
            onLoading?.(isLoading);
        };

        const generateTypedSignature = () => {
            const canvas = document.createElement('canvas');
            canvas.width = 480;
            canvas.height = 120;
            const ctx = canvas.getContext('2d');
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            ctx.font = "italic 40px 'Dancing Script', 'Brush Script MT', cursive";
            ctx.fillStyle = '#172B4D';
            ctx.textBaseline = 'middle';
            ctx.fillText(typedName, 20, 60);
            return canvas.toDataURL('image/png');
        };

        /**
         * Full submit handler — covers all signature modes: saved, draw, type.
         */
        const handleApplySignature = async () => {
            if (!provider && isOtherSignerIncomplete(otherSigner)) {
                setShowSignerErrors(true);
                return;
            }
            if (useSaved) {
                if (!hasSavedSignature || !savedSignature?.url) return;
                try {
                    setChildLoading(true);
                    onLoading?.(true);

                    // Mint a FRESH signed URL from the stored file key
                    // (stored .url may be expired), then fetch the saved
                    // signature bytes and re-upload as a fresh presigned
                    // PUT. useBaseUrl:false so the interceptor doesn't
                    // rewrite the S3 URL.
                    const freshUrl = await getFreshFileUrl(savedSignature, { disposition: 'attachment' });
                    const downloadRes = await axios({
                        url: freshUrl,
                        method: 'GET',
                        responseType: 'blob',
                        useBaseUrl: false,
                    });
                    const blob = new Blob([downloadRes.data]);
                    const file = new File([blob], 'signature.png', { type: 'image/png' });

                    const storedFile = await uploadFile(file, KeyPurpose.SIGNATURE);

                    const info = {
                        file: storedFile,
                        signatureType: provider ? signatureTypeEnums.PROVIDER_SIGNATURE : signatureTypeEnums.SIGNATURE,
                    };
                    if (!provider) Object.assign(info, buildSignerPayload(selectedGuardian, otherSigner));
                    dispatch(appointmentActions.appendSignatureToAppmt(appt.id, info, signatureName));
                    // onApplied?.();
                } catch (e) {
                    const errorMessage = e?.response?.data?.message || e?.data?.message || e?.message || 'Failed to apply signature';
                    toast(<NotError type="error" errMessage={errorMessage} />);
                } finally {
                    setChildLoading(false);
                    onLoading?.(false);
                }
                return;
            }

            // Draw, Type, or Upload mode — upload signature then dispatch
            try {
                setChildLoading(true);
                onLoading?.(true);

                let convertedFile;
                if (activeTab === SIGNATURE_TAB.UPLOAD) {
                    // Uploaded file is already a File — no base64 conversion needed
                    if (!uploadedFile) return;
                    convertedFile = uploadedFile;
                } else {
                    let signatureData;
                    if (activeTab === SIGNATURE_TAB.DRAW) {
                        if (!canvasRef.current?.current) return;
                        signatureData = canvasRef.current.current.getTrimmedCanvas().toDataURL('image/png');
                    } else {
                        if (!typedName.trim()) return;
                        signatureData = generateTypedSignature();
                    }
                    convertedFile = base64ToFile(signatureData, 'signature.png');
                }
                if (!convertedFile) return;

                const storedFile = await uploadFile(convertedFile, KeyPurpose.SIGNATURE);

                const info = {
                    file: storedFile,
                    signatureType: provider ? signatureTypeEnums.PROVIDER_SIGNATURE : signatureTypeEnums.SIGNATURE,
                };
                if (!provider) Object.assign(info, buildSignerPayload(selectedGuardian, otherSigner));

                dispatch(
                    appointmentActions.appendSignatureToAppmt(
                        appt.id,
                        { ...info, signatureLocation: geoPos ? { ...geoPos } : null },
                        signatureName
                    )
                );

                // Save as default signature if checked
                if (saveAsDefault && userInfo?.id) {
                    const storedSignatureFile = await uploadFile(convertedFile, KeyPurpose.SIGNATURE);
                    dispatch(adminActions.createStaffSignature({ file: { ...storedSignatureFile, name: 'staffSignature' } }, userInfo.id));
                }

                // onApplied?.();
            } catch (e) {
                const errorMessage = e?.response?.data?.message || e?.data?.message || e?.message || 'Signature upload failed';
                toast(<NotError type="error" errMessage={errorMessage} />);
            } finally {
                setChildLoading(false);
                onLoading?.(false);
            }
        };

        const tabsContent = [
            {
                tabComponent: (
                    <Draw
                        canvasWidth={fromComplete ? 700 : 480}
                        onCanvasRef={handleCanvasRef}
                        onSaveAsDefaultChange={setSaveAsDefault}
                        onGeoChange={setGeoPos}
                        onDrawingChange={setHasDrawing}
                        hideSaveDefault={hideSaveDefault}
                    />
                ),
                tab: 'Draw',
            },
            {
                tabComponent: (
                    <TypeSignature
                        onNameChange={handleNameChange}
                        onSaveAsDefaultChange={setSaveAsDefault}
                        hideSaveDefault={hideSaveDefault}
                    />
                ),
                tab: 'Type',
            },
            {
                tabComponent: (
                    <SignatureFileUploader
                        onFileChange={handleUploadFileChange}
                        onSaveAsDefaultChange={setSaveAsDefault}
                        hideSaveDefault={hideSaveDefault}
                    />
                ),
                tab: 'Upload',
            },
        ];

        const showGuardian = showGuardianSelector && !provider;

        return (
            <div className="signature-panel" ref={panelRef}>
                {title && <p className="signature-panel-title">{title}</p>}

                {showSavedSignature && hasSavedSignature && savedSignature?.url && (
                    <div className="signature-panel-saved-row">
                        <Checkbox
                            checked={useSaved}
                            onChange={(e) => setUseSaved(e.target.checked)}
                            style={{ padding: 0, color: useSaved ? '#347AF0' : '#D0D5DD' }}
                        />
                        <span className="signature-panel-saved-label">Use my saved signature</span>
                        {savedSignature?.url && <img src={savedSignature.url} alt="saved" className="signature-panel-saved-preview" />}
                    </div>
                )}

                {!useSaved && (
                    <SimpleTabs
                        customTabPanel={'signature-tab-flow-box'}
                        getTab={setActiveTab}
                        tab={activeTab}
                        defaultTab={SIGNATURE_TAB.DRAW}
                        tabsLabels={signatureTabsLabels}
                        tabsContent={tabsContent}
                    />
                )}

                {showGuardian && (
                    <div style={{ width: guardianWidth ? guardianWidth : '35%' }}>
                        <GuardianSelector
                            setSelectedGuardian={setSelectedGuardian}
                            selectedGuardian={selectedGuardian}
                            otherSigner={otherSigner}
                            setOtherSigner={(value) => {
                                setShowSignerErrors(false);
                                setOtherSigner(value);
                            }}
                            showErrors={showSignerErrors}
                        />
                    </div>
                )}

                {!doNotShowAlreadyUploaded && <UploadedSignature currentFile={currentFile} alreadyUploaded={true} provider={provider} />}

                {showFooter && (
                    <div className="signature-panel-footer">
                        {onCancel && (
                            <button className="signature-panel-cancel-btn" onClick={onCancel}>
                                Cancel
                            </button>
                        )}
                        <button
                            className="signature-panel-apply-btn"
                            onClick={handleApplySignature}
                            disabled={loading?.length || childLoading || !hasSignature}>
                            {loading?.length || childLoading ? (
                                <MinLoader position={'relative'} margin={'0'} color={'#fff'} />
                            ) : (
                                submitLabel
                            )}
                        </button>
                    </div>
                )}
            </div>
        );
    }
);

SignaturePanel.displayName = 'SignaturePanel';
